import styles from "./ReasonToBuy.module.scss";
import { ReactNode, useRef, useState, useEffect } from "react";

const MD_SCROLL_MEDIA_QUERY = "(min-width: 600px) and (max-width: 1023px)";

type Card = {
  id: string;
  title: string;
  description?: string;
  icon?: ReactNode;
};

type Props = {
  cards: Card[];
};

export const ReasonToBuyUI = ({ cards }: Props) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(1);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(MD_SCROLL_MEDIA_QUERY);
    const update = () => setCardsPerPage(mq.matches ? 2 : 1);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleScroll = () => {
      const cardWidth = track.scrollWidth / cards.length;
      const pageWidth = cardWidth * cardsPerPage;
      const pageIndex = Math.round(track.scrollLeft / pageWidth);
      const index = pageIndex * cardsPerPage;
      setActiveIndex(Math.min(index, cards.length - 1));
    };

    track.addEventListener("scroll", handleScroll, { passive: true });
    return () => track.removeEventListener("scroll", handleScroll);
  }, [cards.length, cardsPerPage]);

  if (!cards.length) {
    return null;
  }

  const totalPages = Math.ceil(cards.length / cardsPerPage);
  const activePage = Math.floor(activeIndex / cardsPerPage);

  return (
    <div data-component="ReasonToBuy" className={styles.container}>
      <ul
        className={styles.cards}
        data-testid="reason-to-buy-cards-track"
        ref={trackRef}
      >
        {cards.map((card) => (
          <li
            key={card.id}
            className={styles.card}
            data-testid={`reason-to-buy-card-${card.id}`}
          >
            <div className={styles.cardHeader}>
              {card.icon && (
                <span className={styles.cardIcon}>{card.icon}</span>
              )}
            </div>
            <p className={styles.cardTitle}>{card.title}</p>
            {card.description && (
              <p className={styles.cardDescription}>{card.description}</p>
            )}
          </li>
        ))}
      </ul>
      {cards.length > 1 && (
        <div
          className={styles.pagination}
          aria-hidden="true"
          data-testid="reason-to-buy-pagination"
        >
          {Array.from({ length: totalPages }).map((_, pageIndex) => (
            <span
              key={`pagination-dot-${pageIndex}`}
              className={styles.paginationDot}
              data-active={pageIndex === activePage}
            />
          ))}
        </div>
      )}
    </div>
  );
};
